// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Click-to-define glossary. Any element with `data-term` becomes a dotted link;
 * clicking it shows a small tooltip with the definition. Escape or an outside
 * click dismisses it.
 */

export const GLOSSARY: Record<string, string> = {
  'super-resolution':
    'A class of machine-learning model that reconstructs a larger, sharper image from a smaller one — inferring plausible detail rather than just stretching pixels the way a bicubic resize does.',
  swin2sr:
    'Swin2SR — the super-resolution model Embiggen runs. It is a transformer (the "Swin" family) trained to upscale images, converted to ONNX so it runs in your browser.',
  webgpu:
    'A modern browser API that lets a web page run computations on your graphics card. Embiggen uses it to run the model fast, and silently falls back to WASM on the CPU if it is unavailable.',
  wasm:
    'WebAssembly — a portable binary instruction format that runs near-native speed in the browser. The model runtime (ONNX Runtime) is compiled to WASM, so inference works even without WebGPU.',
  onnx:
    'Open Neural Network Exchange — a standard file format for machine-learning models. Embiggen loads Swin2SR as ONNX weights and runs them with ONNX Runtime Web.',
  psnr:
    'Peak Signal-to-Noise Ratio — a measure of how faithful a reconstructed image is to the ideal. The 4× model is tuned for high PSNR, favouring accuracy over invented texture.',
  bicubic:
    'The classic maths-only way browsers enlarge an image: it interpolates between existing pixels. Smooth, but it cannot add real detail — which is what a super-resolution model does.',
};

let tip: HTMLElement | null = null;

function ensureTip(): HTMLElement {
  if (tip) return tip;
  tip = document.createElement('div');
  tip.className = 'glossary-tip';
  tip.hidden = true;
  document.body.appendChild(tip);
  return tip;
}

export function initGlossary(): void {
  document.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const link = target.closest('[data-term]') as HTMLElement | null;
    if (link) {
      ev.preventDefault();
      const term = (link.dataset.term ?? '').toLowerCase();
      const def = GLOSSARY[term];
      if (!def) return;
      const t = ensureTip();
      t.textContent = def;
      t.hidden = false;
      const rect = link.getBoundingClientRect();
      const top = rect.bottom + window.scrollY + 6;
      t.style.top = `${top}px`;
      const left = Math.min(rect.left + window.scrollX, window.innerWidth - 320);
      t.style.left = `${Math.max(8, left)}px`;
      return;
    }
    if (tip && !tip.hidden && !target.closest('.glossary-tip')) {
      tip.hidden = true;
    }
  });
  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && tip && !tip.hidden) tip.hidden = true;
  });
}
